/**
 * The themes. A theme overrides variables and nothing else.
 *
 * Apply one by spreading it onto any element; everything inside sees the new values, so a theme can
 * wrap the whole document or one panel. That is the property worth having: a "preview in dark" pane
 * is the same mechanism as the app-wide setting, not a second implementation of it.
 *
 *   <div {...stylex.props(dark)}>...</div>
 *
 * Two kinds live here. Colour themes (`paper`, `dark`, `appDark`) override `color` only. Density
 * themes (`compact`, `comfortable`) override `size` and the density-following type pairs in `text`,
 * and never a colour, so a dark mode and a density can nest in either order without fighting.
 */
import * as stylex from "@stylexjs/stylex";
import "./breakpoints.stylex.ts";
import { breakpoints } from "./breakpoints.stylex.ts";
import "./color.stylex.ts";
import { color } from "./color.stylex.ts";
import "./size.stylex.ts";
import { size } from "./size.stylex.ts";
import "./text.stylex.ts";
import { text } from "./text.stylex.ts";

/**
 * Printed paper: the formula label and anything else drawn as a physical object. It reads as chalk
 * stock with ink, so a label set inside the dark hero still looks like the label on the pouch.
 *
 * The signed-in roles that alias a brand name follow it here. The rest keep their light values,
 * which is what paper is.
 */
export const paper = {
  xiplds9: "x1t94ljg xiplds9",
  $$css: true
};

/**
 * The dark theme, built on deep teal rather than grey so a dark panel still reads as the brand.
 * `signal` turns luminous mint here, which is the only place it is allowed to glow.
 *
 * The marketing hero and its bands use it. The signed-in roles that alias a brand name follow it,
 * and the status text roles take the light status tones, which pass on teal. A hovered primary
 * button turns mint, the one glow the hero allows. The other hover, selected and subtle roles are
 * not set, so a signed-in component inside this theme draws them in light values. Signed-in
 * screens use `appDark`.
 */
export const dark = {
  xiplds9: "x1lgfkhu xiplds9",
  $$css: true
};

/**
 * The dark mode for signed-in screens, for people who work in the admin or the clinic portal all
 * day. Near-neutral rather than the hero's saturated teal, and `focusRing` is the only colour that
 * glows. It sets every role, and maps each brand name onto the role it aliases so a screen not yet
 * moved to the roles still reads.
 *
 * Nothing applies it yet.
 */
export const appDark = {
  xiplds9: "x1j4hb4y xiplds9",
  $$css: true
};
const compactSize = {
  x1qfrhkd: "x7s0u29 x1qfrhkd",
  $$css: true
};
const compactText = {
  x17hqfif: "x7nzqij x17hqfif",
  $$css: true
};
const comfortableSize = {
  x1qfrhkd: "x170q5iu x1qfrhkd",
  $$css: true
};
const comfortableText = {
  x17hqfif: "xyiiy93 x17hqfif",
  $$css: true
};

/**
 * Dense: clinic and staff tables at a desk. On a coarse pointer, controls, rows and body text go
 * back to their comfortable sizes, so the same screen on a tablet keeps finger-sized targets and
 * 16px inputs. A window narrower than a desktop gets them too, because that is a phone or a tablet far more
 * often than a desk with a thin browser.
 *
 *   <div {...stylex.props(compact)}>...</div>
 */
export const compact = [compactSize, compactText] as const;

/**
 * The customer area's density, and the defaults. Nested inside `compact` it restores them.
 */
export const comfortable = [comfortableSize, comfortableText] as const;